"use client";

import React, { useState } from "react";
import { Phone, AlertCircle } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { useModalController } from "@/components/molecules/Modal/useModalController";
import { PhoneInputField } from "@/components/molecules/PhoneInputField/PhoneInputField";
import { formatPhoneForInput, isValidPhoneNumber, getPhoneDigits } from "@/utils/phone";

export interface PhoneVerificationModalViewProps {
    controller: ReturnType<typeof useModalController>;
    isLoading?: boolean;
    error?: string;
    /** Profile phone to pre-fill (digits or formatted); optional until profile is wired */
    initialPhone?: string;
    onSubmit: (phoneNumber: string) => void | Promise<void>;
    onSkip: () => void;
}

export function PhoneVerificationModalView({
    controller,
    isLoading = false,
    error,
    initialPhone,
    onSubmit,
    onSkip,
}: PhoneVerificationModalViewProps) {
    const [phone, setPhone] = useState(() =>
        initialPhone ? formatPhoneForInput(initialPhone) : ""
    );
    // Validate phone number (10 digits)
    const isValid = isValidPhoneNumber(phone);

    const handlePhoneChange = (value: string) => {
        const formatted = formatPhoneForInput(value);
        setPhone(formatted);
    };

    const handleSubmit = async () => {
        if (isValid && !isLoading) {
            await onSubmit(getPhoneDigits(phone));
        }
    };

    return (
        <div className="w-full max-w-md mx-auto">
            {/* Header with icon */}
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#098487]/15 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#098487]" strokeWidth={2.5} />
                </div>
                <h2 className="text-lg font-bold text-[#232f34]">
                    Add your phone number
                </h2>
            </div>

            <h2 className="text-lg font-bold text-[#232f34] mb-4">
                Please verify or add a cell phone number to associate with your login.
            </h2>

            <p className="text-sm text-[#4b5563] mb-8 leading-relaxed font-medium">
                This number will be used to enable 2-factor authentication via SMS.
            </p>

            <div className="mb-6">
                <label className="block text-xs font-medium text-[#385464] mb-2 uppercase tracking-wide">
                    Phone number
                </label>

                <div className="flex gap-2">
                    {/* Phone Number Input */}
                    <PhoneInputField
                        id="phone"
                        value={phone}
                        onChange={handlePhoneChange}
                        disabled={isLoading}
                        hasError={Boolean(error)}
                        className="flex-1"
                    />
                </div>
            </div>

            {/* Error message */}
            {error && (
                <div className="mb-6 min-h-5">
                    <p className="text-xs text-[#d0464b] flex items-center gap-1.5 font-medium">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {error}
                    </p>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 items-center">
                <Button
                    variant="ghost"
                    onClick={onSkip}
                    disabled={isLoading}
                    className="px-3"
                >
                    Skip for now
                </Button>
                <div className="flex-1" />
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!isValid || isLoading}
                    isLoading={isLoading}
                    className="min-w-[140px]"
                >
                    Confirm
                </Button>
            </div>
        </div>
    );
}
